/**
 * Die Regeln rund um Reisen, Rollen und Mitglieder - rein und ohne Oberfläche.
 *
 * Alles hier ist eine Entscheidung, die auch dann richtig sein muss, wenn
 * niemand hinsieht: welche Reise beim Start aufgeht, wem der Altbestand eines
 * Tagebuchs gehört, welche Adresse überhaupt eine ist. Deshalb steht es hier
 * und nicht verstreut in den Komponenten.
 */

import { ApiError, istRolle, OfflineError, type Member, type Role, type TripSummary } from './api'
import { NIEMAND, type Verfasser } from '../lib/diary'

/**
 * Getrimmt und kleingeschrieben, für den Vergleich zweier Adressen im Client.
 *
 * Ausdrücklich nicht dieselbe Form wie store.NormalizeEmail, auch wenn hier
 * einmal das Gegenteil stand: der Server trimmt, normalisiert nach NFC und
 * schreibt danach ausschliesslich A bis Z klein (mit Begründung, siehe
 * store/trips.go). Hier steht ein toLowerCase() über den ganzen Unicode-Raum
 * und kein NFC. Zwei Adressen, die dort dieselbe sind, können hier also
 * verschieden aussehen und umgekehrt.
 *
 * Folgenlos ist das, weil an dieser Funktion keine Berechtigung hängt. Über
 * Mitgliedschaften entscheidet der Server, und er normalisiert selbst. Hier
 * wird nur verglichen, ob die eigene Adresse die des Besitzers ist
 * (altbestandVerfasser), und ob eine Eingabe überhaupt eine Adresse sein kann
 * (istEmail). Beide Antworten fallen im Zweifel in die harmlose Richtung: es
 * wird nichts überführt, und eine Einladung geht an den Server, der sie prüft.
 */
export function normalisiereEmail(roh: string): string {
  return roh.trim().toLowerCase()
}

/**
 * Die gleiche Formprüfung wie im Server (emailPattern in store/trips.go):
 * etwas vor dem @, etwas dahinter, kein Leerzeichen, kein Schrägstrich.
 *
 * Sie steht hier nicht anstelle der Prüfung dort, sondern davor: eine
 * offensichtlich unbrauchbare Adresse soll gar nicht erst als Netzfehler
 * zurückkommen. Streng zu sein wäre falsch - eingeladen wird auch, wen kein
 * Muster dieser Welt vorhersieht.
 */
export function istEmail(roh: string): boolean {
  const email = normalisiereEmail(roh)
  if (email === '' || email.length > 254) return false
  return /^[^\s@/]+@[^\s@/]+$/.test(email)
}

/** Darf in dieser Reise geschrieben werden? */
export function darfSchreiben(rolle: Role | null | undefined): boolean {
  return rolle === 'modify' || rolle === 'admin'
}

/** Darf diese Reise verwaltet werden - Mitglieder, Name, Löschen? */
export function darfVerwalten(rolle: Role | null | undefined): boolean {
  return rolle === 'admin'
}

/** Wie die Rolle in der Oberfläche heisst. */
export function rolleName(rolle: Role): string {
  switch (rolle) {
    case 'admin':
      return 'Verwalten'
    case 'modify':
      return 'Bearbeiten'
    default:
      return 'Nur lesen'
  }
}

/** Was die Rolle bedeutet, in einem Satz - für die Mitgliederliste. */
export function rolleErklaerung(rolle: Role): string {
  switch (rolle) {
    case 'admin':
      return 'Darf alles ändern und Mitglieder verwalten.'
    case 'modify':
      return 'Darf Etappen, Tagebuch, Ausgaben und Fotos ändern.'
    default:
      return 'Sieht die Reise, ändert nichts.'
  }
}

/**
 * Die Reisen in einer festen Reihenfolge: älteste zuerst, bei gleichem
 * Zeitpunkt nach der Kennung.
 *
 * Die Kennung entscheidet nur den Gleichstand. Zeitstempel haben
 * Millisekundenauflösung, zwei Reisen können also gleich alt sein - und eine
 * Liste, die sich bei jedem Laden anders sortiert, ist keine Liste, in der man
 * etwas wiederfindet.
 */
export function sortiereReisen(trips: readonly TripSummary[]): TripSummary[] {
  return [...trips].sort((a, b) =>
    a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt - b.createdAt,
  )
}

/**
 * Welche Reise beim Start aufgeht - oder keine, dann wird ausgewählt.
 *
 * Der Normalfall darf keinen Klick kosten: wer eine Reise hat, landet in ihr,
 * und wer mehrere hat, landet in der, in der er zuletzt war. Die Auswahl
 * erscheint nur, wenn es tatsächlich etwas zu entscheiden gibt - beim ersten
 * Mal mit mehreren Reisen, oder wenn die zuletzt benutzte nicht mehr dabei ist.
 *
 * @param zuletzt die zuletzt benutzte Reise-ID, soweit gemerkt.
 */
export function waehleReise(
  trips: readonly TripSummary[],
  zuletzt: string | null,
): TripSummary | null {
  if (trips.length === 0) return null
  const gemerkt = zuletzt ? trips.find((t) => t.id === zuletzt) : undefined
  if (gemerkt) return gemerkt
  if (trips.length === 1) return trips[0] ?? null
  return null
}

/**
 * Die Mitglieder in fester Reihenfolge: ältestes zuerst, dann nach Adresse.
 *
 * Dieselbe Überlegung wie bei den Reisen, und hier hängt zusätzlich die
 * Bestimmung des Besitzers daran.
 */
export function sortiereMitglieder(members: readonly Member[]): Member[] {
  return [...members].sort((a, b) =>
    a.createdAt === b.createdAt ? a.email.localeCompare(b.email) : a.createdAt - b.createdAt,
  )
}

/**
 * Wem die Reise gehört: die älteste Mitgliedschaft mit der Rolle admin.
 *
 * Ein Besitzerfeld gibt es nicht, und das ist Absicht (siehe ARCHITECTURE.md,
 * "trips"): Besitz ist eine Mitgliedschaft mit der Rolle admin und sonst
 * nichts. Wer zuerst da war, ist derjenige, aus dessen Bestand die Reise
 * stammt - bei einer migrierten Reise ist das genau der Nutzer, dem vorher
 * alles gehörte.
 *
 * Ausdrücklich nicht createdBy: das ist reine Auskunft und kann auf jemanden
 * zeigen, der längst kein Mitglied mehr ist.
 *
 * @returns die Adresse, oder null wenn die Liste keinen admin enthält - dann
 *   ist sie unvollständig oder gar nicht geladen, und geraten wird nicht.
 */
export function besitzerVon(members: readonly Member[] | null | undefined): string | null {
  if (!members || members.length === 0) return null
  const admins = sortiereMitglieder(members).filter((m) => m.role === 'admin')
  return admins[0]?.email ?? null
}

/**
 * Wer den überführten Tagebuchtext aus der Zeit vor den Beiträgen verfasst
 * hat - oder niemand, dann wird nichts überführt.
 *
 * Der alte Text stammt aus der Zeit, in der eine Reise genau ein Konto hatte:
 * geschrieben hat ihn also der Besitzer der Reise, und niemand sonst. Wer ihn
 * dem zuschreibt, der die App gerade offen hat, verteilt fremde Abende an den
 * Erstbesten, der nach der Freigabe hereinschaut.
 *
 * Deshalb überführt genau ein Gerät: das des Besitzers. Für alle anderen
 * gibt diese Funktion null zurück, und dann geschieht nichts - der alte Text
 * bleibt sichtbar (beiträge() liest ihn weiter mit) und wartet, bis der
 * Besitzer die App das nächste Mal öffnet. Das ist keine Reihenfolge, die
 * jemand einhalten müsste, sondern eine Bedingung, die entweder erfüllt ist
 * oder nicht.
 *
 * Ohne Mitgliederliste - offline, oder der Abruf ist gescheitert - ist die
 * Antwort ebenfalls null. Im Zweifel nichts zu tun ist hier die richtige Wahl:
 * ein nicht überführter Text ist sichtbar und lässt sich morgen überführen,
 * eine falsche Zuschreibung steht danach in jedem gedruckten Buch.
 *
 * @param ich die eigene Identität, wie sie in einen Beitrag geht.
 * @param meineEmail die eigene Adresse - die Mitgliedschaft hängt an ihr, nicht
 *   am sub-Claim.
 * @param besitzer die Adresse des Besitzers, siehe besitzerVon.
 */
export function altbestandVerfasser(
  ich: Verfasser,
  meineEmail: string,
  besitzer: string | null,
): Verfasser | null {
  if (!ich.id) return null
  if (!besitzer) return null
  const meine = normalisiereEmail(meineEmail)
  if (meine === '') return null
  return meine === normalisiereEmail(besitzer) ? ich : null
}

/**
 * Wer als Verfasser des alten Textes angezeigt wird, solange er noch nicht
 * überführt ist.
 *
 * Nicht der Anwesende. Die Kennung bleibt leer - der Text gehört niemandem,
 * den diese App kennt, und niemand darf ihn ändern (darfBearbeiten). Der Name
 * ist die Adresse des Besitzers, denn das ist das Einzige, was wir über ihn
 * wissen: die Mitgliederliste kennt Adressen, keine Anzeigenamen.
 */
export function altbestandAnzeige(verfasser: Verfasser | null, besitzer: string | null): Verfasser {
  if (verfasser) return verfasser
  return besitzer ? { id: '', name: besitzer } : NIEMAND
}

/** Ob eine Rolle aus einer fremden Quelle brauchbar ist. */
export function rolleOderNull(wert: unknown): Role | null {
  return istRolle(wert) ? wert : null
}

/**
 * Was von einem gescheiterten Aufruf in der Oberfläche stehen soll.
 *
 * Ein Satz und keine Nummer. Der wichtigste Fall ist last_admin: dort steht
 * nicht "409", sondern was zu tun ist, damit es geht. Die übrigen Absagen
 * kommen mit einer brauchbaren Meldung vom Server und werden durchgereicht -
 * sie in eine eigene Formulierung zu übersetzen hiesse, sie beim nächsten
 * Serverumbau doppelt pflegen zu müssen.
 */
export function fehlerSatz(err: unknown): string {
  if (err instanceof OfflineError) {
    return 'Der Server ist nicht erreichbar. Sobald wieder Netz da ist, geht es weiter.'
  }
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : 'Unbekannter Fehler'
  }
  if (err.code === 'last_admin') {
    return (
      'Das ist die letzte Person mit der Rolle Verwalten. Ohne sie gehörte die Reise niemandem mehr: ' +
      'erst jemand anderen zu Verwalten machen, dann geht es.'
    )
  }
  if (err.istUnbekannt) {
    return 'Diese Reise gibt es für dich nicht mehr — sie wurde gelöscht, oder die Mitgliedschaft wurde entzogen.'
  }
  if (err.status === 401) {
    return 'Die Anmeldung ist abgelaufen. Nach dem Neuanmelden geht es weiter.'
  }
  return err.message
}
